import styled from 'styled-components';

import { STAGE_WORKSPACE_ATTR } from '../sidepanel/rightPanelDismiss';

/** The picture's rect inside the renderer container, in px. */
export interface CanvasBounds {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface Strip {
  side: 'top' | 'bottom' | 'left' | 'right';
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * Kept clear around the picture so the renderer's resize handles on an
 * element at the frame's edge stay grabbable.
 */
export const LETTERBOX_GUTTER = 16;

/**
 * The letterbox around the picture as up to four strips (top, bottom, left,
 * right), each stopping `gutter` px short of the picture. Strips with no area
 * are dropped.
 */
export function letterboxStrips(
  canvas: CanvasBounds,
  containerWidth: number,
  containerHeight: number,
  gutter: number = LETTERBOX_GUTTER,
): Strip[] {
  if (canvas.width <= 0 || canvas.height <= 0) return [];
  const above = canvas.top - gutter;
  const belowTop = canvas.top + canvas.height + gutter;
  const before = canvas.left - gutter;
  const afterLeft = canvas.left + canvas.width + gutter;
  const strips: Strip[] = [
    { side: 'top', left: 0, top: 0, width: containerWidth, height: above },
    {
      side: 'bottom',
      left: 0,
      top: belowTop,
      width: containerWidth,
      height: containerHeight - belowTop,
    },
    { side: 'left', left: 0, top: 0, width: before, height: containerHeight },
    {
      side: 'right',
      left: afterLeft,
      top: 0,
      width: containerWidth - afterLeft,
      height: containerHeight,
    },
  ];
  return strips.filter(strip => strip.width > 0 && strip.height > 0);
}

interface Props {
  canvas: CanvasBounds;
  containerWidth: number;
  containerHeight: number;
  disabled?: boolean;
}

/**
 * PLAT-12096 — the renderer iframe fills the whole video area, so the dark
 * letterbox around the picture is inside the iframe and a press there never
 * reaches this document. With something selected the renderer reports the
 * deselect; with nothing selected it reports nothing, so a panel opened from
 * the rail stayed open. These transparent strips sit over the letterbox (not
 * the picture) and are marked as stage workspace, so a press on them closes
 * the open panel either way. A drag that starts on the picture keeps going to
 * the iframe when it crosses them: the browser routes the rest of a press to
 * the frame it began in.
 */
export function LetterboxPressTargets({
  canvas,
  containerWidth,
  containerHeight,
  disabled = false,
}: Props) {
  return (
    <>
      {letterboxStrips(canvas, containerWidth, containerHeight).map(
        ({ side, ...rect }) => (
          <StripTarget
            key={side}
            {...{ [STAGE_WORKSPACE_ATTR]: '' }}
            data-testid="stage-letterbox"
            $disabled={disabled}
            style={rect}
          />
        ),
      )}
    </>
  );
}

const StripTarget = styled.div<{ $disabled: boolean }>`
  position: absolute;
  z-index: 1;
  pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};
`;
